import { defineStore } from "pinia";
import { computed, reactive, toRefs, watch } from "vue";
import type { IDomainMenu } from "~/domain/menu";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import type { SalesChannel } from "~/domain/sales-channels";
import { useBasketStore } from "~/stores/basket";
import { useBugReporter, useDirectBackend } from "#imports";

const FRESHNESS_TTL_IN_MS = 60_000;

export type IMenusQuery = {
  businessId: string;
  outletId: string;
  orderingMode: IDomainOrderingMode;
  salesChannel: SalesChannel;
};

type IState = {
  isLoaded: boolean;
  isLoading: boolean;
  menus: IDomainMenu[];
  /** Context the current `menus` belong to, so they can be reloaded. */
  loadedQuery?: IMenusQuery;
  /** Fulfillment time the current `menus` were resolved for. */
  loadedAvailableAt?: string;
  loadedAt?: number;
  /** Set when the customer was routed away from an unavailable menu. */
  hasUnavailableMenuNotice: boolean;
};

function createInitialState(): IState {
  return {
    isLoaded: false,
    isLoading: false,
    menus: [],
    loadedQuery: undefined,
    loadedAvailableAt: undefined,
    loadedAt: undefined,
    hasUnavailableMenuNotice: false,
  };
}

function buildRequestKey(query: IMenusQuery, availableAt?: string) {
  return [
    query.businessId,
    query.outletId,
    query.orderingMode,
    query.salesChannel,
    availableAt ?? "asap",
  ].join("|");
}

export const useMenusStore = defineStore("menus", () => {
  const state = reactive<IState>(createInitialState());

  const { outletRepo } = useDirectBackend();
  const { report } = useBugReporter();
  const basket = useBasketStore();

  function reset() {
    Object.assign(state, createInitialState());
  }

  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  const menuIds = computed(() => state.menus.map((menu) => menu.id));

  const hasOneMenu = computed(() => state.menus.length === 1);

  const firstMenu = computed(() => state.menus?.[0]);

  const isRevalidating = computed(() => state.isLoading && state.isLoaded);

  function isMenuAvailable(menuSlug: string) {
    return state.menus.some((menu) => menu.slug === menuSlug);
  }

  function isSameContext(query: IMenusQuery) {
    const loaded = state.loadedQuery;

    if (!loaded) return false;

    return (
      loaded.businessId === query.businessId &&
      loaded.outletId === query.outletId &&
      loaded.orderingMode === query.orderingMode &&
      loaded.salesChannel === query.salesChannel
    );
  }

  function isResolvedForQuery(query: IMenusQuery) {
    return (
      state.isLoaded &&
      isSameContext(query) &&
      state.loadedAvailableAt === basket.fulfillmentTime
    );
  }

  /** In-flight loads, so concurrent callers share a single request per key. */
  const pendingByRequestKey = new Map<string, Promise<void>>();

  /**
   * Identifies the most recent request, so a response that has been overtaken is
   * discarded instead of overwriting newer menus.
   */
  let latestRequestKey: string | undefined;

  async function fetchMenus(
    query: IMenusQuery,
    requestKey: string,
    availableAt?: string,
  ) {
    latestRequestKey = requestKey;

    // The loaded-list fields stay untouched until the response lands, so callers
    // asking whether the menus match their query are never told yes about a list
    // that is still being replaced.
    patch({ isLoading: true, loadedQuery: query });

    try {
      const response = await outletRepo.getMenus({
        ...query,
        availableAt: availableAt ?? new Date().toISOString(),
      });

      if (latestRequestKey !== requestKey) return;

      patch({
        isLoaded: true,
        isLoading: false,
        menus: response.data,
        loadedAvailableAt: availableAt,
        loadedAt: Date.now(),
      });
    } catch (error) {
      report(error);

      if (latestRequestKey !== requestKey) return;

      patch({
        isLoaded: false,
        isLoading: false,
        menus: [],
        loadedAvailableAt: undefined,
        loadedAt: undefined,
      });
    }
  }

  /**
   * Loads the menus available for `query` at the customer's fulfillment time.
   * Cached while fresh, so it is safe to call on every navigation.
   */
  async function loadMenus(query: IMenusQuery, forceRefresh = false) {
    const availableAt = basket.fulfillmentTime;
    const requestKey = buildRequestKey(query, availableAt);

    const isFresh =
      isResolvedForQuery(query) &&
      state.loadedAt !== undefined &&
      Date.now() - state.loadedAt < FRESHNESS_TTL_IN_MS;

    if (!forceRefresh && isFresh) return;

    const pending = pendingByRequestKey.get(requestKey);
    if (pending) return pending;

    // Menus from another outlet, mode or channel must never be rendered, not even
    // while the new ones load. A different time keeps them until the new list
    // arrives, so changing the time does not blank the page.
    if (state.loadedQuery && !isSameContext(query)) reset();

    const request = fetchMenus(query, requestKey, availableAt).finally(() => {
      pendingByRequestKey.delete(requestKey);
    });

    pendingByRequestKey.set(requestKey, request);

    return request;
  }

  /** Re-resolves the current menus, bypassing the cache. */
  async function refresh() {
    if (!state.loadedQuery) return;

    return loadMenus(state.loadedQuery, true);
  }

  // Re-resolve whenever the customer changes their fulfillment time, from any
  // entry point.
  if (import.meta.client) {
    watch(
      () => basket.fulfillmentTime,
      () => {
        if (!state.loadedQuery) return;

        void loadMenus(state.loadedQuery);
      },
    );
  }

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    menuIds,
    hasOneMenu,
    firstMenu,
    isRevalidating,
    isMenuAvailable,
    isResolvedForQuery,
    loadMenus,
    refresh,
  };
});
